/**
 * F-consolidated-invoice — /faturas: lista de faturas cliente da empresa ativa,
 * filtrável por cliente. Mostra per-dossier e consolidadas (multi-dossier),
 * com download/geração do PDF. Acessível pelo menu Operações.
 */

import { PaymentAllocationModal } from "../components/domain/PaymentAllocationModal.js";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUpRight,
  Bell,
  EllipsisVertical,
  FileText,
  HandCoins,
  Mail,
  Search,
  X,
} from "lucide-react";
import { Link } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { abrirArquivo, useAbaPendente } from "../lib/open-file.js";
import { useGrantOnly } from "../hooks/useGrantOnly.js";
import { InvoiceDetailModal } from "../components/domain/InvoiceDetailModal.js";
import { SendEmailModal, type EmailInvoice } from "../components/domain/SendInvoiceEmailModal.js";
import { VehicleHistoryModal } from "../components/domain/VehicleHistoryModal.js";
import { CreditNoteModal } from "../components/domain/CreditNoteModal.js";
import { InvoiceDraftModal } from "../components/domain/InvoiceDraftModal.js";
import { formatMoney } from "../lib/format-fr.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { useI18n, useT } from "../i18n/index.js";
import { Paginacao } from "../components/common/Paginacao.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FILTER_PANEL_STYLE,
  FilterField,
} from "../components/common/FilterBar.js";

const PAGE_SIZE = 50;

type FiscalStatus = "issued" | "paid" | "cancelled" | "void";

const STATUS_TONE: Record<FiscalStatus, string> = {
  issued: "bg-sky-500/15 text-sky-300",
  paid: "bg-lime/15 text-lime",
  cancelled: "bg-red-500/15 text-red-300",
  void: "bg-neutral-700/40 text-text-secondary",
};

// CEO 2026-10-31 — campos com o estilo único dos painéis de filtro.
const inputClass = FILTER_INPUT;

/**
 * Parse robusto de número digitado em pt-BR/fr-FR: tolera separador de milhar
 * ('.' ou espaço) e decimal (',' ou '.'). O ÚLTIMO separador encontrado é tratado
 * como decimal. Ex.: "1.238,90"→1238.9 · "602,76"→602.76 · "1,234.50"→1234.5 ·
 * "100"→100. Retorna NaN quando não é número (campo vazio inclusive).
 */
function parseDecimalInput(s: string): number {
  const cleaned = s.replace(/\s/g, "");
  if (!cleaned) return NaN;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized: string;
  if (lastComma > lastDot) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = cleaned.replace(/,/g, "");
  } else {
    normalized = cleaned;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

export default function Faturas() {
  const t = useT();
  // F-listas-cartoes — no celular a tabela vira cartões.
  const isMobile = useIsMobile();
  const { locale } = useI18n();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  // F-invoices-redesign (CEO 2026-09-28) — ferramentas de manutenção
  // (relatório do contador, PDFs em massa, duplicados, re-sincronizar) saem
  // do caminho: ficam atrás do botão "Ferramentas".
  const [toolsOpen, setToolsOpen] = useState(false);
  const modals = useModals();
  const statusLabel: Record<FiscalStatus, string> = {
    issued: t("invoices.badgeIssued"),
    paid: t("invoices.badgePaid"),
    cancelled: t("invoices.badgeCancelled"),
    void: t("invoices.badgeVoid"),
  };
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);

  const [customerFilter, setCustomerFilter] = useState<number | null>(null);
  // Deep-link dos KPIs do Painel: /faturas?status=issued|paid abre já filtrado.
  const [statusFilter, setStatusFilter] = useState<FiscalStatus | "all">(() => {
    const p = new URLSearchParams(window.location.search).get("status");
    return (["issued", "paid", "cancelled", "void"] as string[]).includes(p ?? "")
      ? (p as FiscalStatus)
      : "all";
  });
  // Deep-link /faturas?q=F2026-00036 (vindo do painel da fatura na ficha do cliente).
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const [page, setPage] = useState(0);
  // F-fatura-detalhe (CEO 2026-10-12) — painel da fatura ao clicar no nº.
  const [detailId, setDetailId] = useState<number | null>(null);
  // CEO 2026-10-26 — ficha do veículo aberta pela placa da fatura.
  const [fichaVeiculo, setFichaVeiculo] = useState<number | null>(null);
  // F-invoices-edsp — cabeçalho no formato do eDSP: ano + chips com VALORES
  // por status + gráfico mensal. Chip Parcial/A receber usa hasReceipts.
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [receiptFilter, setReceiptFilter] = useState<"with" | "without" | null>(null);
  // CEO 2026-10-19 — chip "Em atraso" (vencida, sem recebimento, não irrecuperável).
  const [overdueFilter, setOverdueFilter] = useState(false);
  // CEO 2026-11-01 — só as emitidas que ninguém enviou (/faturas?naoEnviadas=1).
  const [unsentOnly, setUnsentOnly] = useState(
    () => new URLSearchParams(window.location.search).get("naoEnviadas") === "1",
  );
  // F-fatura-editada (CEO 2026-11-03) — editadas no mesmo número ainda não
  // reenviadas (/faturas?modificadas=1, vindo do sininho/Painel).
  const [amendedOnly, setAmendedOnly] = useState(
    () => new URLSearchParams(window.location.search).get("modificadas") === "1",
  );
  // F-invoices-sort — padrão: nº MAIS RECENTE no topo (CEO 2026-10-07). Os
  // formatos antigos da BS vão para o fim (servidor). Clique no "Nº" inverte;
  // na "Data" ordena pelo dia.
  const [sortBy, setSortBy] = useState<"date_desc" | "number_desc" | "number_asc">("number_desc");
  const [emailFor, setEmailFor] = useState<EmailInvoice | null>(null);
  // F-relance-lote (CEO 2026-10-12): checkbox nas faturas em aberto → "Relancer (N)".
  const [selIds, setSelIds] = useState<ReadonlySet<number>>(new Set());
  const [batchFor, setBatchFor] = useState<EmailInvoice[] | null>(null);
  // F-pay-allocation — quais carros o pagamento parcial já quita.
  const [allocInvoiceId, setAllocInvoiceId] = useState<number | null>(null);
  // CEO 2026-10-27 — rascunhos de fatura (conferência automática) ainda não emitidos.
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftId, setDraftId] = useState<number | null>(null);
  const draftsQ = trpc.invoiceDrafts.list.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null && canEdit },
  );
  const drafts = draftsQ.data?.rows ?? [];
  const [creditNoteFor, setCreditNoteFor] = useState<{ id: number; invoiceNumber: string } | null>(
    null,
  );

  const customersQuery = trpc.customers.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );
  const customers = customersQuery.data?.rows ?? [];

  // Reset paginação quando qualquer filtro muda (senão poderia ficar numa página
  // além do total filtrado, exibindo vazio).
  useEffect(() => {
    setPage(0);
  }, [
    companyId,
    q,
    customerFilter,
    statusFilter,
    sortBy,
    receiptFilter,
    overdueFilter,
    year,
    unsentOnly,
    amendedOnly,
  ]);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      q: q.trim() || undefined,
      customerId: customerFilter ?? undefined,
      fiscalStatus: statusFilter === "all" ? undefined : statusFilter,
      hasReceipts: receiptFilter == null ? undefined : receiptFilter === "with",
      overdueOnly: overdueFilter || undefined,
      unsentOnly: unsentOnly || undefined,
      amendedOnly: amendedOnly || undefined,
      issuedFrom: `${year}-01-01`,
      issuedTo: `${year}-12-31`,
      sortBy,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [
      companyId,
      q,
      customerFilter,
      statusFilter,
      receiptFilter,
      year,
      sortBy,
      page,
      unsentOnly,
      amendedOnly,
    ],
  );
  const edspSummaryQuery = trpc.customerInvoices.edspSummary.useQuery(
    {
      companyId: companyId ?? 0,
      year,
      customerId: customerFilter ?? undefined,
      q: q.trim() || undefined,
    },
    // F-acesso-por-menu — resumos da empresa inteira não vão ao gerente de chantier.
    // CEO 2026-10-19: o gerente também vê os cards — o servidor recorta pelo chantier.
    { enabled: companyId !== null },
  );
  const es = edspSummaryQuery.data;
  const unsentQ = trpc.customerInvoices.unsentSummary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );
  const nUnsent = unsentQ.data?.count ?? 0;
  const nAmended = unsentQ.data?.amendedCount ?? 0;
  // F-invoice-byclient (CEO 2026-09-15) — chip ativo sem cliente selecionado →
  // primeiro a lista de CLIENTES com totais; clicar no cliente abre as faturas.
  const chipBucket = overdueFilter
    ? ("overdue" as const)
    : statusFilter === "paid"
      ? ("paid" as const)
      : statusFilter === "issued" && receiptFilter === "without"
        ? ("awaiting" as const)
        : statusFilter === "issued" && receiptFilter === "with"
          ? ("partial" as const)
          : null;
  const byClientView = chipBucket != null && customerFilter == null;
  const byClientQuery = trpc.customerInvoices.byCustomerSummary.useQuery(
    { companyId: companyId ?? 0, year, bucket: chipBucket ?? "all", q: q.trim() || undefined },
    { enabled: companyId !== null && byClientView },
  );
  const todayIso = new Date().toISOString().slice(0, 10);
  const listQuery = trpc.customerInvoices.list.useQuery(filters, {
    enabled: companyId !== null && !byClientView,
  });
  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;

  const utils = trpc.useUtils();
  // CEO 2026-11-01 — entregue por outro meio: sai do alerta de não enviadas.
  const markSentMut = trpc.customerInvoices.markSentManually.useMutation({
    onSuccess: () => {
      void utils.customerInvoices.list.invalidate();
      void utils.customerInvoices.unsentSummary.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const regenMut = trpc.customerInvoices.regeneratePdf.useMutation({
    onSuccess: () => utils.customerInvoices.list.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: `${t("invoices.pdfErrorPre")}${e.message}`,
        tone: "danger",
      }),
  });

  // F-resync-snapshot — correção assistida: snapshot da fatura ← soma das
  // lines (caso Escoffier: recálculo corrige as linhas, fatura ficava velha).
  const isAdmin =
    user?.isSuperAdmin ||
    (user?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );
  const [resyncOpen, setResyncOpen] = useState(false);
  const [resyncPreview, setResyncPreview] = useState<{
    changes: Array<{
      invoiceId: number;
      invoiceNumber: string;
      oldFaturado: number;
      newFaturado: number;
      receiptOld: number | null;
      receiptNew: number | null;
      receiptNote: string | null;
    }>;
  } | null>(null);
  const resyncMut = trpc.customerInvoices.resyncSnapshotsFromLines.useMutation({
    onSuccess: (r) => {
      if (r.dryRun) {
        setResyncPreview({ changes: r.changes });
      } else {
        setResyncOpen(false);
        setResyncPreview(null);
        utils.customerInvoices.invalidate();
        void modals.alert({
          message: `✓ ${r.changes.length} ${t("invoices.resyncDoneSuf")}`,
        });
      }
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const [attachBusyId, setAttachBusyId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  /**
   * F-bulk-attach — anexa MUITOS PDFs de uma vez, casando cada arquivo com a
   * fatura de mesmo número (nome do arquivo sem extensão = nº da fatura, ex.:
   * "F2025-00248.pdf" → fatura F2025-00248). Resolve os ids num lookup só e
   * envia sequencialmente; ao fim mostra o resumo (anexados / sem match / falhas).
   */
  async function handleBulkAttach(fileList: FileList | null) {
    if (companyId == null || !fileList || fileList.length === 0) return;
    // Aceita seleção de arquivos OU de pasta inteira (webkitdirectory) — filtra só PDFs.
    const files = Array.from(fileList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    if (files.length === 0) return;
    const fileNum = (f: File) =>
      f.name
        .replace(/\.[^.]+$/, "") // remove extensão
        .replace(/\s*\(\d+\)\s*$/, "") // remove sufixo " (1)" de download duplicado
        .trim();
    const numbers = [...new Set(files.map(fileNum).filter(Boolean))];
    if (numbers.length === 0) return;

    setBulkBusy(true);
    setBulkMsg(t("invoices.matchingFiles"));
    try {
      const matches = await utils.customerInvoices.resolveByNumbers.fetch({ companyId, numbers });
      const byNum = new Map(matches.map((m) => [m.number.toUpperCase(), m.id]));

      let ok = 0;
      const unmatched: string[] = [];
      const failed: string[] = [];
      let i = 0;
      for (const f of files) {
        i += 1;
        const id = byNum.get(fileNum(f).toUpperCase());
        if (id == null) {
          unmatched.push(f.name);
          continue;
        }
        setBulkMsg(`${t("invoices.sendingProgressPre")}${i}/${files.length}…`);
        try {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("companyId", String(companyId));
          fd.append("ownerId", String(id));
          fd.append("ownerKind", "customer_invoice");
          const resp = await fetch("/uploads", {
            method: "POST",
            credentials: "include",
            body: fd,
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          ok += 1;
        } catch {
          failed.push(f.name);
        }
      }
      await utils.customerInvoices.list.invalidate();

      const sample = (arr: string[]) =>
        arr.slice(0, 8).join(", ") + (arr.length > 8 ? `, +${arr.length - 8}` : "");
      const lines = [`${t("invoices.bulkOkPre")}${ok}${t("invoices.bulkOkSuf")}`];
      if (unmatched.length)
        lines.push(
          `${t("invoices.bulkUnmatchedPre")}${unmatched.length}${t("invoices.bulkUnmatchedMid")}${sample(unmatched)}`,
        );
      if (failed.length)
        lines.push(
          `${t("invoices.bulkFailedPre")}${failed.length}${t("invoices.bulkFailedMid")}${sample(failed)}`,
        );
      void modals.alert({
        title: t("invoices.bulkDoneTitle"),
        message: lines.join("\n\n"),
        tone: unmatched.length || failed.length ? "warning" : "default",
      });
    } catch (e) {
      void modals.alert({
        title: t("invoices.bulkErrorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    } finally {
      setBulkBusy(false);
      setBulkMsg(null);
    }
  }

  // F-attach-dedup — limpa anexos duplicados (mesmo PDF reanexado). Dry-run
  // primeiro (relata) e só remove após confirmação.
  const dedupeMut = trpc.attachments.dedupe.useMutation();
  async function runDedupe() {
    if (companyId == null) return;
    try {
      const report = await dedupeMut.mutateAsync({ companyId, apply: false });
      if (!report.dryRun) return; // estreita p/ o ramo do dry-run
      if (report.toRemove === 0) {
        void modals.alert({
          title: t("invoices.noDuplicatesTitle"),
          message: t("invoices.noDuplicatesMsg"),
        });
        return;
      }
      const mb = (report.bytesToFree / (1024 * 1024)).toFixed(1);
      const ok = await modals.confirm({
        title: t("invoices.removeDuplicatesTitle"),
        message: `${t("invoices.dedupeConfirmP1")}${report.toRemove}${t("invoices.dedupeConfirmP2")}${report.groupsWithDups}${t("invoices.dedupeConfirmP3")}${mb}${t("invoices.dedupeConfirmP4")}`,
        confirmLabel: t("invoices.removeDuplicatesConfirm"),
        tone: "danger",
      });
      if (!ok) return;
      const res = await dedupeMut.mutateAsync({ companyId, apply: true });
      await utils.attachments.invalidate();
      void modals.alert({
        title: t("invoices.cleanupDoneTitle"),
        message: `${t("invoices.removedDuplicatesPre")}${"removed" in res ? res.removed : 0}${t("invoices.removedDuplicatesSuf")}`,
      });
    } catch (e) {
      void modals.alert({
        title: t("invoices.cleanupErrorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  async function downloadPdf(attachmentId: number) {
    if (companyId == null) return;
    try {
      await abrirArquivo(
        async () =>
          (await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId }))?.url,
      );
    } catch (e) {
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: `${t("invoices.openPdfErrorPre")}${e instanceof Error ? e.message : String(e)}`,
        tone: "danger",
      });
    }
  }

  /**
   * Anexa o PDF EXTERNO/real da fatura (ex.: documento do eDSP) como attachment
   * do customer_invoice. O relatório mensal ao contador usa o anexo mais recente
   * de cada fatura — então este sobrepõe o PDF gerado pelo sistema no relatório.
   * Não altera customerInvoices.attachmentId (o gerado continua disponível).
   */
  async function handleAttach(invoiceId: number, file: File | undefined | null) {
    if (companyId == null || !file) return;
    setAttachBusyId(invoiceId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("companyId", String(companyId));
      fd.append("ownerId", String(invoiceId));
      fd.append("ownerKind", "customer_invoice");
      const resp = await fetch("/uploads", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!resp.ok) {
        const msg = await resp.text().catch(() => "");
        throw new Error(
          msg || `${t("invoices.uploadFailedPre")}${resp.status}${t("invoices.uploadFailedSuf")}`,
        );
      }
      await utils.customerInvoices.list.invalidate();
      void modals.alert({
        title: t("invoices.pdfAttachedTitle"),
        message: t("invoices.pdfAttachedMsg"),
        tone: "default",
      });
    } catch (e) {
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: `${t("invoices.attachPdfErrorPre")}${e instanceof Error ? e.message : String(e)}`,
        tone: "danger",
      });
    } finally {
      setAttachBusyId(null);
    }
  }

  // F-accountant-report — relatório mensal de faturas (PDF com links) p/ contador.
  const [reportMonth, setReportMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const abaRelatorio = useAbaPendente();
  const reportMut = trpc.customerInvoices.monthlyAccountantReport.useMutation({
    onSuccess: (res) => {
      abaRelatorio.ir(res.url);
      void modals.alert({
        title: t("invoices.reportGeneratedTitle"),
        message:
          `${res.invoiceCount}${t("invoices.reportMsgP1")}${formatMoney(res.totalHt)}${t("invoices.reportMsgP2")}` +
          `${formatMoney(res.totalTtc)}${t("invoices.reportMsgP3")}` +
          (res.withoutPdf > 0
            ? `${t("invoices.reportWarnPre")}${res.withoutPdf}${t("invoices.reportWarnSuf")}`
            : ""),
        tone: res.withoutPdf > 0 ? "warning" : "default",
      });
    },
    onError: (e) => {
      abaRelatorio.fechar();
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: `${t("invoices.reportErrorPre")}${e.message}`,
        tone: "danger",
      });
    },
  });

  function generateReport() {
    if (companyId == null) return;
    const parts = reportMonth.split("-");
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) {
      void modals.alert({
        title: t("invoices.invalidMonthTitle"),
        message: t("invoices.invalidMonthMsg"),
        tone: "danger",
      });
      return;
    }
    abaRelatorio.abrir();
    reportMut.mutate({ companyId, year: y, month: m });
  }

  // F-invoices-edsp — chips (cores fixas p/ tema claro, padrão eDSP):
  // Todas cinza · Paga verde · A receber vermelho · Parcial azul.
  const chipDefs = [
    {
      key: "all" as const,
      bg: "#4B5563",
      label: t("invoices.chipAll"),
      data: es?.all,
      on: statusFilter === "all" && receiptFilter == null,
      apply: () => {
        setStatusFilter("all");
        setReceiptFilter(null);
        setOverdueFilter(false);
      },
    },
    {
      key: "paid" as const,
      bg: "#16A34A",
      label: t("invoices.statusPaid"),
      data: es?.paid,
      on: statusFilter === "paid",
      apply: () => {
        setStatusFilter("paid");
        setReceiptFilter(null);
        setOverdueFilter(false);
      },
    },
    {
      key: "awaiting" as const,
      bg: "#DC2626",
      label: t("invoices.chipAwaiting"),
      data: es?.awaiting,
      on: statusFilter === "issued" && receiptFilter === "without" && !overdueFilter,
      apply: () => {
        setStatusFilter("issued");
        setReceiptFilter("without");
        setOverdueFilter(false);
      },
    },
    {
      key: "partial" as const,
      bg: "#2563EB",
      label: t("invoices.chipPartial"),
      data: es?.partial,
      on: statusFilter === "issued" && receiptFilter === "with",
      apply: () => {
        setStatusFilter("issued");
        setReceiptFilter("with");
        setOverdueFilter(false);
      },
    },
    {
      key: "overdue" as const,
      bg: "#B45309",
      label: t("invoices.chipOverdue"),
      data: es?.overdue,
      on: overdueFilter,
      apply: () => {
        setStatusFilter("issued");
        setReceiptFilter("without");
        setOverdueFilter(true);
      },
    },
  ];
  const companyName = accessibleCompanies.find((c) => c.id === companyId)?.name ?? "—";
  const hasFilter = customerFilter != null || q.trim() !== "" || statusFilter !== "all";
  // F-faturas-chips (auditoria UX 23/10/2026, item 8 — L4; CEO 24/10 "pode
  // seguir"): os 5 cartões "Todas / Recebida / A receber / Parcial / Em atraso"
  // pareciam indicadores mas eram filtros, e "Parcial · 0" ocupava um cartão
  // vazio. Viraram chips com contagem (somem quando zerados); o valor do chip
  // ativo vai para o subtítulo. O gráfico "Faturado por mês" saiu daqui: está
  // em Relatórios → Relatório CA (CA por mês).
  const chipAtivo = chipDefs.find((c) => c.on) ?? chipDefs[0]!;
  // tons semânticos (mesmos do resto do ERP) — nada de blocos saturados.
  const kpiTone: Record<(typeof chipDefs)[number]["key"], string> = {
    all: "var(--text-strong)",
    paid: "var(--success-500)",
    awaiting: "var(--danger-500)",
    partial: "var(--info-500)",
    overdue: "var(--warning-500)",
  };

  // F-listas-cartoes — mesmas ações na linha da tabela e no cartão do celular.
  const renderActions = (
    inv: (typeof rows)[number],
    status: FiscalStatus,
    overdue: boolean,
    large = false,
  ) => (
    <InvoiceRowActions
      large={large}
      inv={inv}
      status={status}
      overdue={overdue}
      canEdit={canEdit}
      attachBusy={attachBusyId === inv.id}
      regenPending={regenMut.isPending}
      onPdf={() => inv.attachmentId != null && downloadPdf(inv.attachmentId)}
      onRegen={(lang) => regenMut.mutate({ companyId: companyId!, invoiceId: inv.id, lang })}
      onAttach={(f) => void handleAttach(inv.id, f)}
      onEmail={() => setEmailFor({ id: inv.id, invoiceNumber: inv.invoiceNumber })}
      onRelancer={() =>
        setEmailFor({
          id: inv.id,
          invoiceNumber: inv.invoiceNumber,
          mode: "reminder",
          issuedAt: inv.issuedAt ? String(inv.issuedAt) : null,
          totalTtc: inv.totalTtc,
          totalHt: inv.totalFaturadoSnapshot,
          customerName: inv.customerName,
          receivedHt: inv.receivedHt,
          creditNotesHt: inv.creditNotesHt,
        })
      }
      onAllocate={() => setAllocInvoiceId(inv.id)}
      onAvoir={() => setCreditNoteFor({ id: inv.id, invoiceNumber: inv.invoiceNumber })}
      onMarkSent={async () => {
        const reason = await modals.promptReason({
          title: `${t("invoices.markSentTitle")} — ${inv.invoiceNumber}`,
          placeholder: t("invoices.markSentPh"),
          required: true,
        });
        if (!reason || companyId == null) return;
        markSentMut.mutate({ companyId, invoiceId: inv.id, reason });
      }}
    />
  );

  return (
    <Shell>
      {/* F-invoices-redesign (CEO 2026-09-28: "desorganizado, difícil de
          identificar") — cabeçalho no padrão das telas redesenhadas. */}
      <div className="ph" style={{ marginBottom: 4 }}>
        <div>
          <h1>{t("invoices.pageTitle")}</h1>
          <p data-testid="invoices-subtitle">
            {companyName} · {chipAtivo.data?.count ?? "…"} {t("invoices.pageSubInvoices")}
            {chipAtivo.data != null && ` · ${formatMoney(chipAtivo.data.amount)}`} · {year}
          </p>
        </div>
        <div className="ph__actions">
          <div className="seg">
            {[0, 1, 2].map((d) => {
              const y = new Date().getFullYear() - d;
              return (
                <button key={y} className={y === year ? "on" : ""} onClick={() => setYear(y)}>
                  {y}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className={`btn btn--ghost btn--sm ${toolsOpen ? "!border-lime" : ""}`}
            onClick={() => setToolsOpen((v) => !v)}
            data-testid="invoices-tools-btn"
          >
            🛠 {t("invoices.toolsBtn")} {toolsOpen ? "▴" : "▾"}
          </button>
        </div>
      </div>

      {/* F-faturas-chips (L4) — chips de situação com contagem; o chip ativo é o
          filtro. Somem quando zerados. */}
      <div
        className="flex flex-wrap gap-1.5"
        data-testid="invoices-chips"
        role="group"
        aria-label={t("invoices.chipsAria")}
      >
        {chipDefs
          .filter((c) => c.key === "all" || c.on || (c.data?.count ?? 0) > 0)
          .map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={c.apply}
              data-testid={`invoices-kpi-${c.key}`}
              aria-pressed={c.on}
              title={c.data != null ? formatMoney(c.data.amount) : undefined}
              className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
                c.on
                  ? "border-lime bg-lime text-on-lime"
                  : "border-neutral-700 bg-neutral-900 text-text-secondary hover:border-lime hover:text-lime"
              }`}
            >
              {!c.on && (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: kpiTone[c.key] }}
                />
              )}
              {c.label}
              <span
                className={`rounded-full px-1.5 text-[10px] tabular-nums ${c.on ? "bg-black/10" : "bg-neutral-800 text-text-muted"}`}
              >
                {c.data?.count ?? "…"}
              </span>
            </button>
          ))}
        {drafts.length > 0 && (
          <button
            type="button"
            onClick={() => setDraftsOpen((v) => !v)}
            aria-pressed={draftsOpen}
            data-testid="invoices-drafts-chip"
            className="inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold"
            style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
          >
            📝 {t("invoiceDraft.draftsChip")}
            <span className="rounded-full bg-black/10 px-1.5 text-[10px] tabular-nums">
              {drafts.length}
            </span>
          </button>
        )}
      </div>

      {draftsOpen && drafts.length > 0 && (
        <div
          className="rounded-xl border px-3 py-2.5"
          style={{ background: "var(--surface-card)", borderColor: "#D97706" }}
          data-testid="invoices-drafts-panel"
        >
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-500">
            {t("invoiceDraft.draftsPanelTitle")}
          </p>
          <ul className="divide-y divide-neutral-800">
            {drafts.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => setDraftId(d.id)}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2 text-left text-sm hover:text-lime"
                >
                  <span className="font-semibold">{d.customerName ?? "—"}</span>
                  {d.replacesNumber && (
                    <span
                      className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                      style={{ background: "#FEE2E2", color: "#7F1D1D" }}
                    >
                      ↻ {t("invoiceDraft.replacesPre")} {d.replacesNumber}
                    </span>
                  )}
                  <span className="font-mono text-xs text-neutral-500">{d.placas.join(" · ")}</span>
                  {d.totalHt != null && (
                    <span className="font-mono tabular-nums whitespace-nowrap">
                      {formatMoney(d.totalHt, { decimals: 2 })} HT
                    </span>
                  )}
                  {d.bloqueios > 0 && (
                    <span className="text-xs font-semibold text-red-500 whitespace-nowrap">
                      ⛔ {d.bloqueios} {t("invoiceDraft.blocksWord")}
                    </span>
                  )}
                  {d.avisos > 0 && (
                    <span
                      className="text-xs font-semibold whitespace-nowrap"
                      style={{ color: "#B45309" }}
                    >
                      ⚠ {d.avisos} {t("invoiceDraft.warningsWord")}
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-neutral-500 whitespace-nowrap">
                    {t("invoiceDraft.createdBy")} {d.createdByName ?? "—"} ·{" "}
                    {new Date(d.createdAt).toLocaleDateString("fr-FR")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CEO 2026-11-01 — nenhuma fatura fica parada sem ser enviada. */}
      {(nUnsent > 0 || unsentOnly || amendedOnly) && (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-3"
          style={{ background: "var(--warning-50)", borderColor: "var(--warning-500)" }}
          data-testid="invoices-unsent-banner"
        >
          <span className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
            ✉ {nUnsent} {nUnsent === 1 ? t("invoices.unsentOne") : t("invoices.unsentMany")}
          </span>
          <span className="text-xs" style={{ color: "var(--text-body)" }}>
            {t("invoices.unsentHint")}
          </span>
          <button
            type="button"
            onClick={() => {
              if (unsentOnly) {
                setUnsentOnly(false);
                return;
              }
              setStatusFilter("all");
              setReceiptFilter(null);
              setOverdueFilter(false);
              setCustomerFilter(null);
              setAmendedOnly(false);
              setUnsentOnly(true);
            }}
            aria-pressed={unsentOnly}
            className={(unsentOnly ? CHIP_ON : CHIP_OFF) + " ml-auto"}
            data-testid="invoices-unsent-toggle"
          >
            {unsentOnly ? t("invoices.unsentShowAll") : t("invoices.unsentShowOnly")}
          </button>
          {/* F-fatura-editada — o devis mudou e a fatura foi refeita no mesmo número. */}
          {(nAmended > 0 || amendedOnly) && (
            <button
              type="button"
              onClick={() => {
                if (amendedOnly) {
                  setAmendedOnly(false);
                  return;
                }
                setStatusFilter("all");
                setReceiptFilter(null);
                setOverdueFilter(false);
                setCustomerFilter(null);
                setUnsentOnly(false);
                setAmendedOnly(true);
              }}
              aria-pressed={amendedOnly}
              className={amendedOnly ? CHIP_ON : CHIP_OFF}
              data-testid="invoices-amended-toggle"
            >
              ✎ {t("invoices.chipAmended")} · {nAmended}
            </button>
          )}
        </div>
      )}

      {/* Filtros numa linha só — CEO 2026-10-31: rótulos, campos com borda
          visível e botão "Limpar filtros" com ícone (mesmo padrão de Carros). */}
      <div
        className="flex flex-wrap items-end gap-3 rounded-xl border p-4"
        style={FILTER_PANEL_STYLE}
        data-testid="invoices-filter-panel"
      >
        <FilterField
          label={t("invoices.filterSearchLabel")}
          className="min-w-[240px] max-w-xl grow"
        >
          <div className="relative">
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-muted)" }}
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("invoices.searchPlaceholder")}
              className={inputClass + " w-full pl-9"}
              data-testid="invoices-search"
            />
          </div>
        </FilterField>
        {accessibleCompanies.length > 1 && (
          <FilterField label={t("invoices.filterCompanyLabel")} className="w-full sm:w-auto">
            <select
              value={companyId ?? ""}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              className={inputClass + " w-full sm:w-56"}
              title={t("invoices.companyLabel")}
            >
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FilterField>
        )}
        <FilterField label={t("invoices.colCustomer")} className="w-full sm:w-auto">
          <select
            value={customerFilter ?? ""}
            onChange={(e) => setCustomerFilter(e.target.value ? Number(e.target.value) : null)}
            className={
              inputClass + " w-full sm:w-60" + (customerFilter != null ? " !border-lime" : "")
            }
          >
            <option value="">{t("invoices.allCustomers")}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label={t("invoices.filterStatusLabel")} className="w-full sm:w-auto">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as FiscalStatus | "all");
              setReceiptFilter(null);
            }}
            className={
              inputClass + " w-full sm:w-44" + (statusFilter !== "all" ? " !border-lime" : "")
            }
          >
            <option value="all">{t("invoices.allStatuses")}</option>
            <option value="issued">{t("invoices.statusIssued")}</option>
            <option value="paid">{t("invoices.statusPaid")}</option>
            <option value="cancelled">{t("invoices.statusCancelled")}</option>
            <option value="void">{t("invoices.statusVoid")}</option>
          </select>
        </FilterField>
        {hasFilter && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setCustomerFilter(null);
              setStatusFilter("all");
              setReceiptFilter(null);
            }}
            className={CHIP_OFF}
            title={t("invoices.clearSearchTitle")}
            data-testid="invoices-clear-filters"
          >
            <X size={15} aria-hidden="true" />
            {t("invoices.clearFilters")}
          </button>
        )}
      </div>

      {/* F-resync-snapshot — modal de preview antes/depois. */}
      {resyncOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl rounded-lg border border-neutral-800 bg-neutral-950 shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-4">
              <h2 className="text-lg font-semibold">⚖ {t("invoices.resyncTitle")}</h2>
              <button
                onClick={() => setResyncOpen(false)}
                className="text-lg leading-none text-neutral-500 hover:text-neutral-300"
              >
                ×
              </button>
            </div>
            <div className="space-y-4 p-5">
              <p className="text-sm text-neutral-400">{t("invoices.resyncHelp")}</p>
              {resyncMut.isPending && !resyncPreview && (
                <p className="text-sm text-neutral-500">{t("invoices.resyncLoading")}</p>
              )}
              {resyncPreview && resyncPreview.changes.length === 0 && (
                <p className="rounded-md border border-lime/40 bg-lime/10 px-3 py-2 text-sm text-lime">
                  ✓ {t("invoices.resyncEmpty")}
                </p>
              )}
              {resyncPreview && resyncPreview.changes.length > 0 && (
                <div className="overflow-x-auto rounded border border-neutral-800">
                  <table className="w-full text-sm">
                    <thead className="border-b border-neutral-800 text-xs uppercase text-neutral-500">
                      <tr>
                        <th className="px-3 py-2 text-left">{t("invoices.resyncColInvoice")}</th>
                        <th className="px-3 py-2 text-right">{t("invoices.resyncColOld")}</th>
                        <th className="px-3 py-2 text-right">{t("invoices.resyncColNew")}</th>
                        <th className="px-3 py-2 text-right">{t("invoices.resyncColReceipt")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-900">
                      {resyncPreview.changes.map((c) => (
                        <tr key={c.invoiceId}>
                          <td className="px-3 py-1.5 font-mono text-xs text-lime">
                            {c.invoiceNumber}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-red-300">
                            {formatMoney(c.oldFaturado, { decimals: 2 })}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-lime">
                            {formatMoney(c.newFaturado, { decimals: 2 })}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs text-neutral-400">
                            {c.receiptNote
                              ? c.receiptNote
                              : c.receiptOld != null && c.receiptNew != null
                                ? `${formatMoney(c.receiptOld, { decimals: 2 })} → ${formatMoney(c.receiptNew, { decimals: 2 })}`
                                : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end gap-2 border-t border-neutral-800 pt-3">
                <button
                  onClick={() => setResyncOpen(false)}
                  className="rounded-md border border-neutral-800 px-4 py-1.5 text-sm hover:border-neutral-700"
                >
                  {t("invoices.resyncClose")}
                </button>
                {resyncPreview && resyncPreview.changes.length > 0 && (
                  <button
                    onClick={() => resyncMut.mutate({ companyId: companyId!, dryRun: false })}
                    disabled={resyncMut.isPending}
                    className="rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright disabled:opacity-50"
                  >
                    {t("invoices.resyncApplyPre")} {resyncPreview.changes.length}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {toolsOpen && (
        <div
          className="space-y-3 rounded-xl border px-4 py-3"
          style={{ background: "var(--surface-card)", borderColor: "var(--border)" }}
          data-testid="invoices-tools"
        >
          {/* F-accountant-report — relatório mensal de faturas (PDF com links) p/ contador. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-neutral-300">
              {"📑 "}
              {t("invoices.monthlyReportLabel")}
            </span>
            <input
              type="month"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              className={inputClass}
            />
            <button
              onClick={generateReport}
              disabled={reportMut.isPending || companyId == null}
              className="rounded border border-lime/50 bg-lime/10 px-3 py-1.5 text-sm text-lime hover:bg-lime/20 disabled:opacity-40"
            >
              {reportMut.isPending ? t("invoices.generating") : t("invoices.generateReportBtn")}
            </button>
            <span className="text-[11px] text-neutral-500">{t("invoices.reportHint")}</span>
          </div>

          {/* F-bulk-attach — anexar muitos PDFs de uma vez, casando pelo nome do arquivo. */}
          {canEdit && (
            <div
              className="flex flex-wrap items-center gap-2 border-t pt-3"
              style={{ borderColor: "var(--border)" }}
            >
              <span className="text-xs font-medium text-neutral-300">
                {"📎 "}
                {t("invoices.bulkAttachLabel")}
              </span>
              <label
                className={`cursor-pointer rounded border border-lime/50 bg-lime/10 px-3 py-1.5 text-sm text-lime hover:bg-lime/20 ${
                  bulkBusy || companyId == null ? "pointer-events-none opacity-40" : ""
                }`}
                title={t("invoices.bulkSelectTitle")}
              >
                {bulkBusy ? (bulkMsg ?? t("invoices.sending")) : t("invoices.selectPdfs")}
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  disabled={bulkBusy || companyId == null}
                  onChange={(e) => {
                    void handleBulkAttach(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
              <span className="text-xs text-neutral-500">{t("invoices.or")}</span>
              <label
                className={`cursor-pointer rounded border border-sky-500/50 bg-sky-500/10 px-3 py-1.5 text-sm text-sky-300 hover:bg-sky-500/20 ${
                  bulkBusy || companyId == null ? "pointer-events-none opacity-40" : ""
                }`}
                title={t("invoices.folderSelectTitle")}
              >
                {"📁 "}
                {t("invoices.selectFolder")}
                <input
                  type="file"
                  className="hidden"
                  disabled={bulkBusy || companyId == null}
                  ref={(el) => {
                    if (el) {
                      el.setAttribute("webkitdirectory", "");
                      el.setAttribute("directory", "");
                    }
                  }}
                  onChange={(e) => {
                    void handleBulkAttach(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
              <span className="text-[11px] text-neutral-500">
                {t("invoices.bulkHintPre")}
                <code>F2025-00248.pdf</code>
                {t("invoices.bulkHintOr")}
                <code>F-2025-248.pdf</code>
                {t("invoices.bulkHintMid")}
                <strong>{t("invoices.bulkHintStrong")}</strong>
                {t("invoices.bulkHintSuf")}
              </span>
              {(user?.isSuperAdmin === true ||
                (user?.memberships ?? []).some(
                  (m) => m.companyId === companyId && m.active && m.role === "admin",
                )) && (
                <button
                  type="button"
                  onClick={() => void runDedupe()}
                  disabled={dedupeMut.isPending || companyId == null}
                  title={t("invoices.dedupeTitle")}
                  className="ml-auto rounded border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300 hover:bg-amber-500/20 disabled:opacity-40"
                >
                  {dedupeMut.isPending ? (
                    t("invoices.checking")
                  ) : (
                    <>
                      {"🧹 "}
                      {t("invoices.cleanDuplicates")}
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {isAdmin && (
            <div
              className="flex flex-wrap items-center gap-2 border-t pt-3"
              style={{ borderColor: "var(--border)" }}
            >
              <span className="text-xs font-medium text-neutral-300">
                ⚖ {t("invoices.resyncBtn")}
              </span>
              <button
                onClick={() => {
                  setResyncOpen(true);
                  setResyncPreview(null);
                  resyncMut.mutate({ companyId: companyId!, dryRun: true });
                }}
                className="rounded border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-600 dark:text-amber-300 hover:border-amber-500"
                title={t("invoices.resyncBtnTitle")}
              >
                {t("invoices.resyncRun")}
              </button>
              <span className="text-[11px] text-neutral-500">{t("invoices.resyncBtnTitle")}</span>
            </div>
          )}
        </div>
      )}

      {/* F-invoice-byclient — visão por CLIENTE (chip ativo, sem cliente escolhido) */}
      {byClientView && (
        <>
          <div className="dz-tbl-wrap">
            {byClientQuery.isLoading && (
              <p className="p-4 text-sm text-neutral-500">{t("invoices.loading")}</p>
            )}
            {byClientQuery.error && (
              <p className="p-4 text-sm text-red-400">{byClientQuery.error.message}</p>
            )}
            {byClientQuery.data && byClientQuery.data.length === 0 && (
              <p className="p-4 text-sm text-neutral-500">{t("invoices.emptyState")}</p>
            )}
            {byClientQuery.data && byClientQuery.data.length > 0 && (
              <table className="dz-tbl">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2">{t("invoices.colCustomer")}</th>
                    <th className="text-right px-3 py-2 w-24">{t("invoices.byClientColCount")}</th>
                    <th className="text-right px-3 py-2 w-36 font-mono">
                      {t("invoices.colTotal")}
                    </th>
                    <th className="text-right px-3 py-2 w-48">
                      {t("invoices.byClientColOverdue")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {byClientQuery.data.map((c) => (
                    <tr
                      key={c.customerId}
                      className="cursor-pointer hover:bg-neutral-800/40"
                      onClick={() => setCustomerFilter(c.customerId)}
                    >
                      <td className="px-3 py-2.5">
                        <span className="font-medium text-lime hover:underline">
                          {c.customerName}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-neutral-300">
                        {c.count}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold">
                        {formatMoney(c.total, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {c.overdueCount > 0 ? (
                          <span
                            className="font-mono tabular-nums text-xs font-semibold"
                            style={{ color: "#DC2626" }}
                          >
                            ⚠ {c.overdueCount} · {formatMoney(c.overdueTotal, { decimals: 2 })}
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-neutral-500 -mt-2">{t("invoices.byClientHint")}</p>
        </>
      )}

      {chipBucket != null && customerFilter != null && (
        <button
          onClick={() => setCustomerFilter(null)}
          className="self-start rounded-md border border-neutral-700 hover:border-neutral-500 px-3 py-1.5 text-xs text-neutral-300"
        >
          ← {t("invoices.backAllClients")}
        </button>
      )}

      {!byClientView && (
        <>
          <div className="dz-tbl-wrap">
            {selIds.size > 0 && (
              <div
                className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
                data-testid="reminder-batch-bar"
              >
                <span className="text-amber-200">
                  {selIds.size}{" "}
                  {selIds.size === 1 ? t("invoices.selectedOne") : t("invoices.selectedMany")}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setBatchFor(
                      rows
                        .filter((r) => selIds.has(r.id))
                        .map((r) => ({
                          id: r.id,
                          invoiceNumber: r.invoiceNumber,
                          mode: "reminder" as const,
                          issuedAt: r.issuedAt ? String(r.issuedAt) : null,
                          totalTtc: r.totalTtc,
                          totalHt: r.totalFaturadoSnapshot,
                          customerName: r.customerName,
                          receivedHt: r.receivedHt,
                          creditNotesHt: r.creditNotesHt,
                        })),
                    )
                  }
                  className="rounded-md border border-red-500/60 bg-red-500/10 px-3 py-1 text-sm font-semibold text-red-300 hover:bg-red-500/20"
                  data-testid="reminder-batch-btn"
                >
                  {t("invoices.relancerBtn")} ({selIds.size})
                </button>
                <button
                  type="button"
                  onClick={() => setSelIds(new Set())}
                  className="text-xs text-neutral-400 hover:text-neutral-200"
                >
                  {t("invoices.clearSelection")}
                </button>
              </div>
            )}
            {!isMobile && (
              <table className="dz-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>
                      {canEdit && (
                        <input
                          type="checkbox"
                          aria-label={t("invoices.selectAllOpenTitle")}
                          title={t("invoices.selectAllOpenTitle")}
                          checked={
                            rows.some((r) => r.fiscalStatus === "issued" && !r.uncollectibleAt) &&
                            rows
                              .filter((r) => r.fiscalStatus === "issued" && !r.uncollectibleAt)
                              .every((r) => selIds.has(r.id))
                          }
                          onChange={(e) =>
                            setSelIds(
                              e.target.checked
                                ? new Set(
                                    rows
                                      .filter(
                                        (r) => r.fiscalStatus === "issued" && !r.uncollectibleAt,
                                      )
                                      .map((r) => r.id),
                                  )
                                : new Set(),
                            )
                          }
                          className="accent-lime"
                        />
                      )}
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() =>
                        setSortBy((s) => (s === "number_desc" ? "number_asc" : "number_desc"))
                      }
                      title={t("invoices.sortByNumberTitle")}
                    >
                      {t("invoices.colInvoiceNumber")}
                      {sortBy === "number_desc" ? " ▼" : sortBy === "number_asc" ? " ▲" : ""}
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => setSortBy("date_desc")}
                      title={t("invoices.sortByDateTitle")}
                    >
                      {t("invoices.colDate")}
                      {sortBy === "date_desc" ? " ▼" : ""}
                    </th>
                    <th>{t("invoices.colCustomer")}</th>
                    <th className="r">{t("invoices.colCars")}</th>
                    <th className="r">{t("invoices.colTotalHt")}</th>
                    <th>{t("invoices.colSituation")}</th>
                    <th className="r">{t("invoices.colActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.isLoading && (
                    <tr>
                      <td colSpan={8} className="text-center text-neutral-500">
                        {t("invoices.loading")}
                      </td>
                    </tr>
                  )}
                  {!listQuery.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-neutral-500">
                        {t("invoices.emptyState")}
                      </td>
                    </tr>
                  )}
                  {rows.map((inv) => {
                    const { status, irrecuperavel, overdue, saldo, partial, sit, dt } = invoiceView(
                      inv,
                      todayIso,
                      t,
                    );
                    return (
                      <tr
                        key={inv.id}
                        style={{
                          cursor: "default",
                          opacity: status === "cancelled" || status === "void" ? 0.55 : 1,
                        }}
                      >
                        <td>
                          {canEdit && status === "issued" && !irrecuperavel && (
                            <input
                              type="checkbox"
                              checked={selIds.has(inv.id)}
                              onChange={(e) =>
                                setSelIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(inv.id);
                                  else next.delete(inv.id);
                                  return next;
                                })
                              }
                              title={t("invoices.selectForReminderTitle")}
                              className="accent-lime"
                              data-testid={`invoice-select-${inv.id}`}
                            />
                          )}
                        </td>
                        <td>
                          {/* CEO 2026-09-14: o nº abre o dossier do carro. A de vários
                            carros não tem dossier único → texto com tooltip. */}
                          {/* CEO 2026-10-12: o nº abre o PAINEL da fatura (ações,
                            carros, recebimentos) — antes a consolidada não
                            fazia nada e a per-dossier saltava para o dossier. */}
                          <button
                            type="button"
                            onClick={() => setDetailId(inv.id)}
                            className="font-mono font-semibold hover:underline"
                            style={{ color: "var(--text-link)" }}
                            title={t("invoices.openDetailTitle")}
                            data-testid={`invoice-open-${inv.id}`}
                          >
                            {inv.invoiceNumber}
                          </button>
                          {inv.creditNoteNumbers.length > 0 && (
                            <span
                              className="ml-1.5 text-[10px] text-text-muted"
                              title={inv.creditNoteNumbers.join(", ")}
                            >
                              ↳ {t("invoices.avoirBtn")} {inv.creditNoteNumbers.length}
                            </span>
                          )}
                        </td>
                        <td className="tabular-nums text-text-muted">
                          {dt
                            ? dt.toLocaleDateString(locale, {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td style={{ whiteSpace: "normal", maxWidth: 280 }}>
                          <span
                            className="block truncate font-medium"
                            style={{ color: "var(--text-strong)" }}
                          >
                            {inv.customerName ?? "—"}
                          </span>
                          {/* CEO 2026-10-26 — placa e modelo do(s) carro(s): um carro =
                              placa · modelo; vários = 1ª placa + "+N" (todas no
                              título; a lista completa fica no painel da fatura). */}
                          {inv.vehicles.length > 0 && (
                            <span
                              className="block truncate text-[11px] text-text-muted"
                              data-testid={`invoice-vehicles-${inv.id}`}
                              title={inv.vehicles.map((v) => v.plate ?? v.vin ?? "—").join(", ")}
                            >
                              🚗{" "}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const v0 = inv.vehicles[0]!;
                                  if (v0.vehicleId != null) setFichaVeiculo(v0.vehicleId);
                                }}
                                className="font-mono hover:text-lime hover:underline"
                              >
                                {inv.vehicles[0]!.plate ?? inv.vehicles[0]!.vin ?? "—"}
                              </button>
                              {inv.vehicles.length === 1 ? (
                                [inv.vehicles[0]!.make, inv.vehicles[0]!.model].filter(Boolean)
                                  .length > 0 && (
                                  <>
                                    {" · "}
                                    {[inv.vehicles[0]!.make, inv.vehicles[0]!.model]
                                      .filter(Boolean)
                                      .join(" ")}
                                  </>
                                )
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDetailId(inv.id);
                                  }}
                                  className="ml-1 hover:text-lime hover:underline"
                                  title={t("invoices.vehiclesMoreHint")}
                                >
                                  +{inv.vehicles.length - 1}
                                </button>
                              )}
                            </span>
                          )}
                          {/* CEO 2026-09-14: técnico que reparou, na própria linha. */}
                          {inv.technicianNames.length > 0 && (
                            <span
                              className="block truncate text-[11px] text-text-muted"
                              title={inv.technicianNames.join(", ")}
                            >
                              🔧{" "}
                              {inv.technicianNames.length <= 2
                                ? inv.technicianNames.join(", ")
                                : `${inv.technicianNames.slice(0, 2).join(", ")} +${inv.technicianNames.length - 2}`}
                            </span>
                          )}
                        </td>
                        <td className="r tabular-nums">
                          {inv.isConsolidated ? (
                            <span
                              className="badge b-violet"
                              title={t("invoices.consolidatedExplain")}
                              data-testid="invoice-multi-cars"
                            >
                              🚗 {inv.dossierCount}
                            </span>
                          ) : (
                            <span className="text-text-muted">{inv.dossierCount}</span>
                          )}
                        </td>
                        <td
                          className="r font-mono tabular-nums"
                          style={{ color: "var(--text-strong)" }}
                        >
                          {formatMoney(inv.totalFaturadoSnapshot, { decimals: 2 })}
                          {partial && (
                            <span className="block text-[10px] font-sans text-text-muted">
                              {t("invoices.remainingPre")} {formatMoney(saldo, { decimals: 2 })}
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${sit.cls}`}
                            title={
                              overdue ? `${t("invoices.overdueSince")} ${inv.dueAt}` : undefined
                            }
                          >
                            <span className="led" />
                            {sit.label}
                          </span>
                          {inv.naoEnviada && (
                            <span
                              className="mt-0.5 block text-[10px] font-semibold"
                              style={{ color: "var(--warning-500)" }}
                              title={t("invoices.unsentBadgeTitle")}
                              data-testid={`invoice-unsent-${inv.id}`}
                            >
                              ✉ {t("invoices.unsentBadge")}
                            </span>
                          )}
                          {inv.amendedAt && (
                            <span
                              className="mt-0.5 block text-[10px] font-semibold"
                              style={{ color: "var(--warning-500)" }}
                              data-testid={`invoice-amended-${inv.id}`}
                            >
                              ✎ {t("invoices.badgeAmended")} v{inv.amendVersion}
                            </span>
                          )}
                          {/* CEO 2026-10-12 — a data do pagamento ao lado da situação. */}
                          {inv.lastReceivedAt && (status === "paid" || partial) && (
                            <span className="block text-[10px] text-text-muted tabular-nums">
                              {t("invoices.receivedOnPre")}{" "}
                              {new Date(inv.lastReceivedAt).toLocaleDateString(locale, {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })}
                            </span>
                          )}
                        </td>
                        <td className="r">{renderActions(inv, status, overdue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {isMobile && (
              <div data-testid="invoices-cards" className="divide-y divide-neutral-900">
                {listQuery.isLoading && (
                  <p className="p-4 text-center text-sm text-neutral-500">
                    {t("invoices.loading")}
                  </p>
                )}
                {!listQuery.isLoading && rows.length === 0 && (
                  <p className="p-4 text-center text-sm text-neutral-500">
                    {t("invoices.emptyState")}
                  </p>
                )}
                {rows.map((inv) => {
                  const { status, irrecuperavel, overdue, saldo, partial, sit, dt, diasAtraso } =
                    invoiceView(inv, todayIso, t);
                  const selecionavel = canEdit && status === "issued" && !irrecuperavel;
                  return (
                    <div
                      key={inv.id}
                      data-testid="invoice-card"
                      className="flex gap-3 px-3 py-3"
                      style={{ opacity: status === "cancelled" || status === "void" ? 0.55 : 1 }}
                    >
                      {selecionavel ? (
                        <input
                          type="checkbox"
                          checked={selIds.has(inv.id)}
                          onChange={(e) =>
                            setSelIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(inv.id);
                              else next.delete(inv.id);
                              return next;
                            })
                          }
                          aria-label={inv.invoiceNumber}
                          className="mt-1 h-5 w-5 shrink-0 accent-lime"
                          data-testid={`invoice-select-${inv.id}`}
                        />
                      ) : (
                        <span className="w-5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => setDetailId(inv.id)}
                            className="whitespace-nowrap font-mono text-base font-semibold hover:underline"
                            style={{ color: "var(--text-link)" }}
                            title={t("invoices.openDetailTitle")}
                            data-testid={`invoice-open-${inv.id}`}
                          >
                            {inv.invoiceNumber}
                          </button>
                          <span
                            className={`badge ${sit.cls} shrink-0`}
                            title={
                              overdue ? `${t("invoices.overdueSince")} ${inv.dueAt}` : undefined
                            }
                          >
                            <span className="led" />
                            {sit.label}
                            {overdue &&
                              diasAtraso > 0 &&
                              ` · ${diasAtraso} ${diasAtraso === 1 ? t("invoices.dayWord") : t("invoices.daysWord")}`}
                          </span>
                          {inv.naoEnviada && (
                            <span
                              className="badge shrink-0"
                              style={{
                                background: "var(--warning-50)",
                                color: "var(--warning-500)",
                              }}
                              title={t("invoices.unsentBadgeTitle")}
                            >
                              ✉ {t("invoices.unsentBadge")}
                            </span>
                          )}
                          {inv.amendedAt && (
                            <span
                              className="badge shrink-0"
                              style={{
                                background: "var(--warning-50)",
                                color: "var(--warning-500)",
                              }}
                            >
                              ✎ {t("invoices.badgeAmended")} v{inv.amendVersion}
                            </span>
                          )}
                        </div>
                        <div
                          className="mt-1 break-words text-sm font-medium"
                          style={{ color: "var(--text-strong)" }}
                        >
                          {inv.customerName ?? "—"}
                        </div>
                        {inv.vehicles.length > 0 && (
                          <div
                            className="truncate font-mono text-[11px] text-text-secondary"
                            title={inv.vehicles.map((v) => v.plate ?? v.vin ?? "—").join(", ")}
                          >
                            🚗 {inv.vehicles[0]!.plate ?? inv.vehicles[0]!.vin ?? "—"}
                            {inv.vehicles.length === 1
                              ? [inv.vehicles[0]!.make, inv.vehicles[0]!.model].filter(Boolean)
                                  .length > 0
                                ? ` · ${[inv.vehicles[0]!.make, inv.vehicles[0]!.model].filter(Boolean).join(" ")}`
                                : ""
                              : ` +${inv.vehicles.length - 1}`}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-text-muted">
                          <span className="tabular-nums">
                            {dt
                              ? dt.toLocaleDateString(locale, {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })
                              : "—"}
                          </span>
                          <span>
                            {inv.isConsolidated ? "🚗 " : ""}
                            {inv.dossierCount}{" "}
                            {inv.dossierCount === 1
                              ? t("dashboard.carWord")
                              : t("dashboard.carsWord")}
                          </span>
                          {inv.technicianNames.length > 0 && (
                            <span className="truncate" title={inv.technicianNames.join(", ")}>
                              🔧{" "}
                              {inv.technicianNames.length <= 2
                                ? inv.technicianNames.join(", ")
                                : `${inv.technicianNames.slice(0, 2).join(", ")} +${inv.technicianNames.length - 2}`}
                            </span>
                          )}
                          {inv.creditNoteNumbers.length > 0 && (
                            <span title={inv.creditNoteNumbers.join(", ")}>
                              ↳ {t("invoices.avoirBtn")} {inv.creditNoteNumbers.length}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-end justify-between gap-2">
                          <div
                            className="font-mono tabular-nums"
                            style={{ color: "var(--text-strong)" }}
                          >
                            <span className="text-base font-semibold">
                              {formatMoney(inv.totalFaturadoSnapshot, { decimals: 2 })}
                            </span>
                            {partial && (
                              <span className="block text-[10px] font-sans text-text-muted">
                                {t("invoices.remainingPre")} {formatMoney(saldo, { decimals: 2 })}
                              </span>
                            )}
                            {inv.lastReceivedAt && (status === "paid" || partial) && (
                              <span className="block text-[10px] font-sans text-text-muted">
                                {t("invoices.receivedOnPre")}{" "}
                                {new Date(inv.lastReceivedAt).toLocaleDateString(locale, {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })}
                              </span>
                            )}
                          </div>
                          <div className="ml-auto flex flex-wrap justify-end">
                            {renderActions(inv, status, overdue, true)}
                          </div>
                        </div>
                        {/* F-faturas-chips (L1/L3) — as duas ações da fatura à mão:
                            Cobrar (relance) quando atrasada e Registrar recebimento
                            (abre o carro no lugar certo; fatura de vários carros
                            abre o painel). */}
                        {canEdit && status === "issued" && !irrecuperavel && (
                          <div
                            className="mt-2 grid grid-cols-2 gap-2"
                            data-testid={`invoice-card-actions-${inv.id}`}
                          >
                            {overdue ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setEmailFor({
                                    id: inv.id,
                                    invoiceNumber: inv.invoiceNumber,
                                    mode: "reminder",
                                    issuedAt: inv.issuedAt ? String(inv.issuedAt) : null,
                                    totalTtc: inv.totalTtc,
                                    totalHt: inv.totalFaturadoSnapshot,
                                    customerName: inv.customerName,
                                    receivedHt: inv.receivedHt,
                                    creditNotesHt: inv.creditNotesHt,
                                  })
                                }
                                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-lime px-3 text-sm font-semibold text-on-lime"
                                data-testid={`card-collect-${inv.id}`}
                              >
                                <Bell size={15} aria-hidden="true" /> {t("invoices.collectBtn")}
                              </button>
                            ) : (
                              <span />
                            )}
                            {inv.dossierId != null ? (
                              <Link
                                href={`/dossiers/${inv.dossierId}?acao=receber`}
                                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-neutral-700 px-3 text-sm font-semibold text-text-secondary hover:border-lime hover:text-lime"
                                data-testid={`card-receipt-${inv.id}`}
                              >
                                <HandCoins size={15} aria-hidden="true" />{" "}
                                {t("invoices.registerReceiptBtn")}
                              </Link>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setDetailId(inv.id)}
                                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-neutral-700 px-3 text-sm font-semibold text-text-secondary hover:border-lime hover:text-lime"
                                data-testid={`card-open-${inv.id}`}
                              >
                                <ArrowUpRight size={15} aria-hidden="true" />{" "}
                                {t("invoices.openInvoiceBtn")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Paginacao
            total={total}
            pageSize={PAGE_SIZE}
            offset={page * PAGE_SIZE}
            onOffset={(o) => setPage(Math.floor(o / PAGE_SIZE))}
            noun={t("invoices.pageSubInvoices")}
            disabled={listQuery.isLoading}
            testId="invoices-pagination"
          />
        </>
      )}

      {emailFor && companyId != null && (
        <SendEmailModal
          companyId={companyId}
          invoice={emailFor}
          onClose={() => setEmailFor(null)}
        />
      )}
      {fichaVeiculo != null && companyId != null && (
        <VehicleHistoryModal
          companyId={companyId}
          vehicleId={fichaVeiculo}
          onClose={() => setFichaVeiculo(null)}
        />
      )}
      {detailId != null && companyId != null && (
        <InvoiceDetailModal
          companyId={companyId}
          invoiceId={detailId}
          onClose={() => setDetailId(null)}
          onEmail={(i) => {
            setDetailId(null);
            setEmailFor({ id: i.id, invoiceNumber: i.invoiceNumber });
          }}
          onRelancer={(i) => {
            setDetailId(null);
            setEmailFor({ ...i, mode: "reminder" });
          }}
          onAvoir={(i) => {
            setDetailId(null);
            setCreditNoteFor(i);
          }}
          onAllocate={(id) => {
            setDetailId(null);
            setAllocInvoiceId(id);
          }}
        />
      )}
      {batchFor && batchFor.length > 0 && companyId != null && (
        <SendEmailModal
          companyId={companyId}
          invoice={batchFor[0]!}
          batch={batchFor}
          onClose={() => setBatchFor(null)}
          onSent={() => setSelIds(new Set())}
        />
      )}
      {draftId != null && companyId != null && (
        <InvoiceDraftModal
          companyId={companyId}
          draftId={draftId}
          onClose={() => setDraftId(null)}
        />
      )}
      {allocInvoiceId != null && companyId != null && (
        <PaymentAllocationModal
          companyId={companyId}
          invoiceId={allocInvoiceId}
          onClose={() => setAllocInvoiceId(null)}
          onSaved={() => void utils.customerInvoices.invalidate()}
        />
      )}
      {creditNoteFor && companyId != null && (
        <CreditNoteModal
          companyId={companyId}
          invoice={creditNoteFor}
          onClose={() => setCreditNoteFor(null)}
        />
      )}
    </Shell>
  );
}

/**
 * F-invoices-redesign (CEO 2026-09-28) — ações da linha: PDF e e-mail à vista
 * (os mais usados), "Cobrar" quando atrasada, e o resto num menu ⋮ que abre
 * ancorado à janela (mesmo padrão do /dossiers — não é cortado pela tabela).
 */
/**
 * F-listas-cartoes — situação de uma fatura, calculada num lugar só (tabela
 * do desktop e cartões do celular): atraso, saldo, parcial, selo.
 */
function invoiceView(
  inv: {
    fiscalStatus: string;
    uncollectibleAt: unknown;
    hasReceipts: boolean;
    dueAt: string | null;
    totalFaturadoSnapshot: string | number | null;
    creditNotesHt: string | number | null;
    receivedHt: string | number | null;
    issuedAt: string | Date | null;
  },
  todayIso: string,
  t: ReturnType<typeof useT>,
) {
  const status = inv.fiscalStatus as FiscalStatus;
  // F-invoice-relance — atrasada = emitida, échéance vencida, não irrecuperável.
  // F-saldo-unico (CEO 2026-11-03, falha 3): paga em parte continua atrasada.
  const irrecuperavel = status === "issued" && inv.uncollectibleAt != null;
  const overdue =
    status === "issued" && !irrecuperavel && inv.dueAt != null && inv.dueAt < todayIso;
  // saldo HT = fatura − avoirs emitidos − recebido (embedded já está no valor)
  const totalHt = Number(inv.totalFaturadoSnapshot ?? 0);
  const saldo = Math.max(0, totalHt - Number(inv.creditNotesHt ?? 0) - Number(inv.receivedHt ?? 0));
  const partial = status === "issued" && inv.hasReceipts && saldo > 0.01;
  const sit: { cls: string; label: string } = irrecuperavel
    ? { cls: "b-mut", label: t("invoices.sitUncollectible") }
    : status === "paid"
      ? { cls: "b-ok", label: t("invoices.badgePaid") }
      : status === "cancelled"
        ? { cls: "b-mut", label: t("invoices.badgeCancelled") }
        : status === "void"
          ? { cls: "b-mut", label: t("invoices.badgeVoid") }
          : overdue
            ? { cls: "b-dang", label: t("invoices.overdueBadge") }
            : partial
              ? { cls: "b-warn", label: t("invoices.chipPartial") }
              : { cls: "b-info", label: t("invoices.sitAwaiting") };
  const dt = inv.issuedAt ? new Date(inv.issuedAt) : null;
  // F-faturas-chips — "Em atraso · 37 dias": dias desde a échéance.
  const diasAtraso =
    overdue && inv.dueAt
      ? Math.max(0, Math.round((Date.parse(todayIso) - Date.parse(inv.dueAt)) / 86400000))
      : 0;
  return { status, irrecuperavel, overdue, saldo, partial, sit, dt, diasAtraso };
}

function InvoiceRowActions({
  inv,
  status,
  overdue,
  canEdit,
  attachBusy,
  regenPending,
  onPdf,
  onRegen,
  onAttach,
  onEmail,
  onRelancer,
  onAllocate,
  onAvoir,
  onMarkSent,
  large = false,
}: {
  inv: {
    attachmentId: number | null;
    dossierId: number | null;
    naoEnviada: boolean;
    isConsolidated: boolean;
    dossierCount: number;
    hasReceipts: boolean;
    allocatedDossierCount: number;
  };
  status: FiscalStatus;
  overdue: boolean;
  canEdit: boolean;
  attachBusy: boolean;
  regenPending: boolean;
  onPdf: () => void;
  onRegen: (lang: "fr" | "en" | "pt" | undefined) => void;
  onAttach: (f: File | undefined) => void;
  onEmail: () => void;
  onRelancer: () => void;
  onAllocate: () => void;
  onAvoir: () => void;
  /** CEO 2026-11-01 — marcar como enviada fora do Gestion. */
  onMarkSent?: () => void;
  /** F-listas-cartoes — alvos de toque de 40 px no cartão do celular. */
  large?: boolean;
}) {
  const t = useT();
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const live = status === "issued" || status === "paid";
  const canAllocate =
    status === "issued" && inv.isConsolidated && inv.dossierCount > 1 && inv.hasReceipts;
  const iconBtn = `inline-flex ${large ? "h-10 w-10 text-base" : "h-7 w-7"} items-center justify-center rounded-md border border-neutral-800 text-text-secondary hover:border-lime hover:text-lime transition-colors disabled:opacity-40`;
  const item =
    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs hover:bg-[var(--surface-muted)]";
  const close = () => setPos(null);
  return (
    <div className="inline-flex items-center justify-end gap-1">
      {canEdit && overdue && !large && (
        <button
          onClick={onRelancer}
          className={`inline-flex ${large ? "h-10 px-3" : "h-7 px-2"} items-center rounded-md border border-red-500/50 bg-red-500/10 text-xs font-medium text-red-400 hover:border-red-500`}
          title={t("invoices.relancerTitle")}
        >
          {t("invoices.relancerBtn")}
        </button>
      )}
      <button
        onClick={onPdf}
        disabled={inv.attachmentId == null}
        className={iconBtn}
        title={inv.attachmentId != null ? t("invoices.pdfBtn") : t("invoices.noPdfYet")}
        aria-label={t("invoices.pdfBtn")}
      >
        <FileText size={large ? 18 : 15} aria-hidden="true" />
      </button>
      {canEdit && live && (
        <button
          onClick={onEmail}
          className={iconBtn}
          title={t("invoices.emailTitle")}
          aria-label={t("invoices.emailTitle")}
        >
          <Mail size={large ? 18 : 15} aria-hidden="true" />
        </button>
      )}
      {canEdit && (
        <button
          type="button"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const right = Math.max(8, window.innerWidth - r.right);
            // perto do fim da tela → abre para CIMA (senão o menu sai cortado)
            setPos(
              pos
                ? null
                : window.innerHeight - r.bottom < 300
                  ? { bottom: window.innerHeight - r.top + 4, right }
                  : { top: r.bottom + 4, right },
            );
          }}
          className={iconBtn + (pos ? " !border-lime !text-lime" : "")}
          title={t("invoices.colActions")}
          aria-label={t("invoices.colActions")}
          data-testid="invoice-row-menu"
        >
          <EllipsisVertical size={large ? 18 : 15} aria-hidden="true" />
        </button>
      )}
      {pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={close} />
            <div
              className="fixed z-50 min-w-[240px] rounded-lg border p-1 shadow-xl"
              style={{
                top: pos.top,
                bottom: pos.bottom,
                right: pos.right,
                background: "var(--surface-card)",
                borderColor: "var(--border-strong)",
                color: "var(--text-body)",
              }}
            >
              {/* CEO 2026-10-31 — o dossier do carro direto do menu da linha
                  (também na fatura cancelada). Vários carros → painel da fatura. */}
              {inv.dossierId != null && (
                <>
                  <Link
                    href={`/dossiers/${inv.dossierId}`}
                    onClick={close}
                    className={item + " font-semibold"}
                    data-testid="invoice-menu-open-dossier"
                  >
                    🚗 {t("invoices.openDossierBtn")}
                  </Link>
                  <div className="my-1 h-px" style={{ background: "var(--divider)" }} />
                </>
              )}
              {inv.naoEnviada && onMarkSent && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      onMarkSent();
                    }}
                    className={item}
                    data-testid="invoice-menu-mark-sent"
                  >
                    ✓ {t("invoices.markSentMenu")}
                  </button>
                  <div className="my-1 h-px" style={{ background: "var(--divider)" }} />
                </>
              )}
              <div
                className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wider text-text-muted"
                title={
                  inv.attachmentId != null
                    ? t("invoices.regeneratePdfTitle")
                    : t("invoices.generatePdfTitle")
                }
              >
                {inv.attachmentId != null ? t("invoices.pdfMenuRegen") : t("invoices.pdfMenuGen")}
              </div>
              {(
                [
                  [undefined, t("invoices.langAuto")],
                  ["fr", "Français"],
                  ["en", "English"],
                  ["pt", "Português"],
                ] as const
              ).map(([lang, label]) => (
                <button
                  key={label}
                  disabled={regenPending}
                  onClick={() => {
                    close();
                    onRegen(lang);
                  }}
                  className={item + " pl-6"}
                  data-testid={
                    lang == null ? (inv.attachmentId != null ? "regen-pdf" : "gen-pdf") : undefined
                  }
                >
                  ↻ {label}
                </button>
              ))}
              <div className="my-1 h-px" style={{ background: "var(--divider)" }} />
              <label
                className={
                  item + " cursor-pointer" + (attachBusy ? " pointer-events-none opacity-40" : "")
                }
              >
                📎 {t("invoices.attachMenu")}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    close();
                    onAttach(e.target.files?.[0]);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
              {canAllocate && (
                <button
                  onClick={() => {
                    close();
                    onAllocate();
                  }}
                  className={item}
                  title={t("paymentAllocation.invoiceBtnTitle")}
                  data-testid="invoice-allocation-btn"
                >
                  🚗 {t("invoices.allocateMenu")} ({inv.allocatedDossierCount}/{inv.dossierCount})
                </button>
              )}
              {live && (
                <button
                  onClick={() => {
                    close();
                    onAvoir();
                  }}
                  className={item}
                  title={t("invoices.creditNoteTitle")}
                  data-testid="invoice-menu-avoir"
                >
                  ↩ {t("invoices.avoirMenu")}
                </button>
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

/**
 * F-avoir — Modal de avoir (nota de crédito) sobre uma fatura. Lista os avoirs
 * existentes da fatura + form de criação (nº editável A-YYYY-NNNN, tipo, HT, TVA,
 * motivo, data). O tipo decide o impacto: comercial/devolução/erro reduzem CA;
 * irrecuperável é perda (não reduz CA). Todos abatem o saldo a receber.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-6xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
